import { useQuery, useMutation } from '@tanstack/react-query';
import {
  updateConsultStatus,
  waitingRoomDetails,
  CallHeartBeatAPI,
  PutConsultOnHold,
} from '../services/waitingService';
import type {
  ConsultStatusResponce,
  WaitingService,
  HeartBeatResponse,
  PutConsultOnHoldResponse,
} from '../services/waitingService';
import { REFRESH_INTERVAL } from '@qler-view/shared_lib/src/shared/constants/CommanConstants';

export const useWaitingRoomDetails = (consultId: number, userId: number) => {
  const { data, isLoading, isError, error, refetch } = useQuery<WaitingService>(
    {
      queryKey: ['WaitingRoomDetails', consultId],
      queryFn: async () => {
        const data = await waitingRoomDetails(consultId, userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      refetchInterval: REFRESH_INTERVAL,
      enabled: !!consultId,
      refetchIntervalInBackground: true,
    }
  );

  return {
    waitingRoomData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useUpdateConsultStatus = (
  consultId: number,
  userId: number = 0
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ConsultStatusResponce>({
      queryKey: ['consultStatus', consultId, userId],
      queryFn: async () => {
        const data = await updateConsultStatus(consultId, userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      refetchInterval: REFRESH_INTERVAL,
      enabled: consultId !== null && consultId !== undefined,
      refetchIntervalInBackground: true,
    });

  return {
    consultStatus: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCallHeartBeat = (
  consultId: number,
  portalType: string,
  userId: number,
  enabled: boolean
) => {
  const { data, isLoading, isError, error } = useQuery<HeartBeatResponse>({
    queryKey: ['heartbeat', consultId],
    queryFn: async () => {
      const data = await CallHeartBeatAPI(consultId, portalType, userId);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    refetchInterval: REFRESH_INTERVAL,
    enabled: enabled && consultId !== null && consultId !== undefined,
    refetchIntervalInBackground: true,
    retry: 1,
  });

  return {
    heartbeatData: data,
    isLoading,
    isError,
    error,
  };
};

export interface PutConsultOnHoldParams {
  consultId: number;
  userId: number;
}

export const usePutConsultOnHold = () => {
  return useMutation<PutConsultOnHoldResponse, Error, PutConsultOnHoldParams>({
    mutationFn: async ({ consultId, userId }: PutConsultOnHoldParams) => {
      return await PutConsultOnHold(consultId, userId);
    },
  });
};
